/**
 * Контракт `GET /api/v1/profile-summary/:sub` — сводка профиля для ШАПКИ
 * стороннего сервиса (INTEGRATION-GUIDE §8).
 *
 * Зачем отдельный эндпоинт, а не расширение `/api/entitlements/:sub`: сервису
 * для меню аккаунта нужны сразу имя, аватар, маскированная почта, баланс
 * Баллов и уровень IEC+. Собирать это тремя запросами на каждый рендер шапки
 * — гарантированный N+1 на чужой стороне, а класть профиль в контракт прав
 * значит смешать два разных SoT в одном ответе.
 *
 * Три правила, из которых собран весь остальной формат:
 *
 *   1. **Полная почта наружу не уходит никогда** (инвариант №5) — только
 *      `maskedEmail`. Поля `email` в этом контракте нет и быть не должно.
 *   2. **Сервис получает ровно то, что разрешила связь `links`.** Не выданный
 *      data-scope означает ОТСУТСТВИЕ поля, а не ошибку и не `null`.
 *   3. **Отсутствие поля по недоступности ≠ отсутствие по правам.** Первое
 *      перечислено в `degraded`, второе — нет. Без этого различия сервис не
 *      знает, стирать ли свой кэш (грабли №6: тихая деградация).
 */
import { z } from "zod";
import {
	CurrencySchema,
	IsoDateTimeSchema,
	LocaleSchema,
	MaskedEmailSchema,
	MoneyMinorSchema,
	PointsAmountSchema,
	SubjectSchema,
} from "../_shared/primitives.js";
import { EntitlementSourceSchema, IecPlusTierSchema } from "../entitlements/schemas.js";
import { ServiceDataScopeSchema } from "../links/schemas.js";
import { AgeGroupSchema } from "./schemas.js";

/** Путь эндпоинта. Держим здесь, чтобы сервер и документация не разъехались. */
export const PROFILE_SUMMARY_ROUTE = "/api/v1/profile-summary/:sub";

/**
 * Узкая запись фамилии сервисом, у которого уже есть связь с человеком.
 *
 * Полный `profile.update` сервисам не открываем: имя, дата рождения и ник
 * правятся только владельцем в витрине. Фамилия в анкете Портала — исключение:
 * её спрашивают на шаге «Личные данные», и без этого шва значение остаётся
 * только в MSSQL, а IEC ID при следующем входе затирает его пустым.
 */
export const PROFILE_LAST_NAME_ROUTE = "/api/v1/profile/:sub";

export const ProfileLastNameBodySchema = z.object({
	lastName: z.string().trim().min(1).max(100),
});

export const ProfileLastNameResponseSchema = z.object({
	sub: SubjectSchema,
	lastName: z.string().min(1).max(100),
});

export type ProfileLastNameBody = z.infer<typeof ProfileLastNameBodySchema>;
export type ProfileLastNameResponse = z.infer<typeof ProfileLastNameResponseSchema>;

/**
 * Разделы витрины IEC ID: имя раздела → путь.
 *
 * Единственный список на всю систему. Его читают:
 *   • `POST /api/bridge/handoff` — поле `section` разрешается по нему в адрес
 *     ВНУТРИ `ID_WEB_URL` (открытым редиректом такое значение стать не может);
 *   • этот эндпоинт — собирает `loyaltyUrl` и `manageUrl`.
 *
 * Почему имя раздела, а не путь в запросе сервиса: пути витрины — наше
 * внутреннее устройство. Сервис, зашивший у себя `/points`, ломается в день
 * переезда раздела; `section: "points"` переживает переезд.
 *
 * Ключи совпадают с файловыми маршрутами `apps/id-web/src/routes/`.
 * Новый раздел витрины = новая строка ЗДЕСЬ, иначе сервисы о нём не узнают.
 */
export const ID_SECTIONS = {
	home: "/",
	personal: "/personal",
	security: "/security",
	points: "/points",
	plus: "/plus",
	services: "/services",
	notifications: "/notifications",
	legal: "/legal",
} as const;

export type IdSection = keyof typeof ID_SECTIONS;

export const IdSectionSchema = z.enum(Object.keys(ID_SECTIONS) as [IdSection, ...IdSection[]]);

/**
 * Скоуп ключа сервиса (`service_clients.scopes`), без которого эндпоинт не
 * отвечает. Отдельный, а не `entitlements.read`: чтение прав и чтение профиля
 * с балансом — разный объём данных, и выдаются они разным сервисам по-разному.
 */
export const PROFILE_SUMMARY_SERVICE_SCOPE = "profile.read";

/**
 * Сколько сервису держать ответ. 60 секунд — компромисс: баланс меняется
 * событиями, а не ежесекундно, а ошибка на минуту в шапке не стоит запроса на
 * каждый рендер. Настоящая инвалидация — SET-события `points.balance-changed`,
 * `entitlement-changed`, `profile-updated`: TTL здесь только потолок
 * расхождения на случай, если событие не доехало.
 */
export const PROFILE_SUMMARY_CACHE_TTL_SEC = 60;

/** Что именно оказалось недоступно, когда поле пропало не по правам. */
export const ProfileSummaryDegradationSchema = z.enum(["profile", "points", "plan"]);

/** Баланс Баллов. Требует data-scope `points_balance`. */
export const ProfileSummaryPointsSchema = z.object({
	/** Целое. Баллы никогда не float (арх. правило №8). */
	balance: PointsAmountSchema.nonnegative(),
	/** Сколько это стоит по фиксированному курсу 100 Баллов = 1 USD. */
	worthMinor: MoneyMinorSchema.nonnegative(),
	currency: CurrencySchema,
	/**
	 * Дата ближайшего сгорания. `null` — сгорать нечему. Показывать «когда-нибудь»
	 * нельзя: дата обязана быть видна человеку (план §4.1).
	 */
	nextExpiryAt: IsoDateTimeSchema.nullable(),
});

/** Действующий уровень IEC+. Требует data-scope `entitlements`. */
export const ProfileSummaryPlanSchema = z.object({
	/** Сервис берёт МАКСИМУМ из своей локальной подписки и этого значения. */
	tier: IecPlusTierSchema,
	/** Откуда право. `null` при `tier = free` — записи в реестре нет. */
	source: EntitlementSourceSchema.nullable(),
	expiresAt: IsoDateTimeSchema.nullable(),
});

export const ProfileSummaryParamsSchema = z.object({ sub: SubjectSchema });

export const ProfileSummaryResponseSchema = z.object({
	sub: SubjectSchema,
	/**
	 * Есть ли ЖИВАЯ связь человека с этим сервисом. `false` — человек в сервис
	 * не входил либо отключил его в витрине: личных полей не будет вовсе.
	 * Аккаунт впрок при этом не заводится (инвариант №5).
	 */
	linked: z.boolean(),
	/** Что разрешила связь. Пустой массив при `linked: false`. */
	scopes: z.array(ServiceDataScopeSchema),

	// ── личные поля: каждое приходит только со своим data-scope ─────────────
	/** scope `name`. */
	displayName: z.string().min(1).max(201).optional(),
	/**
	 * scope `name`, те же данные в пригодной для записи форме. Сервис, который
	 * держит имя, фамилию и ник в разных колонках, склеенной строкой обойтись
	 * не может: разрезать её обратно нельзя, а ника в ней нет вовсе.
	 */
	firstName: z.string().min(1).max(100).optional(),
	lastName: z.string().min(1).max(100).optional(),
	nickname: z.string().min(1).max(100).optional(),
	/** scope `picture`. */
	avatarUrl: z.url().optional(),
	/** scope `email`. ПОЛНОЙ почты в этом контракте нет (инвариант №5). */
	maskedEmail: MaskedEmailSchema.optional(),
	/** scope `locale`. */
	locale: LocaleSchema.optional(),
	/** scope `age_group`. Возраст проверяется один раз у нас (инвариант №4). */
	ageGroup: AgeGroupSchema.optional(),
	/** scope `points_balance`. */
	points: ProfileSummaryPointsSchema.optional(),
	/** scope `entitlements`. */
	plan: ProfileSummaryPlanSchema.optional(),

	// ── навигация ──────────────────────────────────────────────────────────
	/**
	 * Куда вести по клику на баланс. Обычная ссылка на витрину: чтобы человек
	 * попал туда УЖЕ ВОШЕДШИМ, сервис зовёт `POST /api/bridge/handoff`
	 * с `section: "points"` и редиректит на выданный `handoffUrl`.
	 */
	loyaltyUrl: z.url(),
	/** Куда ведёт «Управлять аккаунтом IEC» — корень витрины. */
	manageUrl: z.url(),

	/**
	 * Что не удалось прочитать ПРЯМО СЕЙЧАС. Непустой список означает: поля нет
	 * из-за нашей недоступности, права ни при чём — сервис обязан оставить своё
	 * прежнее значение, а не затирать его пустотой (инвариант №14).
	 */
	degraded: z.array(ProfileSummaryDegradationSchema),
	generatedAt: IsoDateTimeSchema,
});

export type ProfileSummaryResponse = z.infer<typeof ProfileSummaryResponseSchema>;

// ─────────────── Пакетная форма `POST /api/v1/profile-summary` ───────────────

/**
 * Путь пакетного чтения. Отдельный маршрут, а не query-параметр к одиночному:
 * список `sub` не помещается в URL надёжно (у Портала их сотни на страницу
 * участников), а POST с телом не упирается ни в длину строки, ни в чужие прокси.
 *
 * ЗАЧЕМ ОН ВООБЩЕ. Одиночный эндпоинт при лимите 600/мин означает, что экран
 * со списком на 200 человек стоит 200 запросов, а два таких экрана подряд уже
 * упираются в потолок. Это тот самый N+1, только по HTTP и на чужой стороне.
 */
export const PROFILE_SUMMARY_BATCH_ROUTE = "/api/v1/profile-summary";

/**
 * Потолок одного запроса. 100 — не круглое число ради красоты: каждый `sub`
 * стоит до трёх чтений (профиль, Баллы, права), и пачка больше сотни держала бы
 * соединение достаточно долго, чтобы чужой таймаут превратил успешный ответ в
 * ретрай всей пачки.
 */
export const PROFILE_SUMMARY_BATCH_MAX = 100;

export const ProfileSummaryBatchBodySchema = z.object({
	subs: z.array(SubjectSchema).min(1).max(PROFILE_SUMMARY_BATCH_MAX),
});

export const ProfileSummaryBatchResponseSchema = z.object({
	/**
	 * Сводки в том же виде, что у одиночного эндпоинта. Порядок совпадает с
	 * порядком запрошенных `sub`, за вычетом неизвестных.
	 */
	items: z.array(ProfileSummaryResponseSchema),
	/**
	 * `sub`, которых у нас нет. Отдельным списком, а не пропуском в `items`:
	 * иначе сервис не отличит «человека нет» от «мы его потеряли по дороге» и
	 * либо оставит навсегда пустую карточку, либо будет опрашивать его вечно.
	 *
	 * Дубликаты во входе схлопываются, поэтому длина `items` + `unknown` может
	 * быть меньше длины `subs`.
	 */
	unknown: z.array(SubjectSchema),
	generatedAt: IsoDateTimeSchema,
});

export type ProfileSummaryBatchBody = z.infer<typeof ProfileSummaryBatchBodySchema>;
export type ProfileSummaryBatchResponse = z.infer<typeof ProfileSummaryBatchResponseSchema>;
export type ProfileSummaryPoints = z.infer<typeof ProfileSummaryPointsSchema>;
export type ProfileSummaryPlan = z.infer<typeof ProfileSummaryPlanSchema>;
export type ProfileSummaryDegradation = z.infer<typeof ProfileSummaryDegradationSchema>;
